import {
  type ListComputedWithPPGDataResponse,
  type ListMedicalDataResponse,
  type ListPPGDataResponse,
  type ListPPGDerivedDataWithPPGDataResponse,
  type ListSymptomResponse,
  PPGDerivedDataGroup,
  PPGQualityFlag,
} from "@/api";
import { DERIVED_MEAN_TYPE } from "@/features/patient/common/derived-data-names";

import { dayFormatter } from "./daily-monitoring-logic";

/** A single latest metric reading, or `null` when the patient has none. */
export type MetricValue = { value: number; unit: string | null } | null;

/**
 * The day the worklist is showing, and the timezone that defines it — the same
 * pair `todayInTimezone` produces. Readings from any other day don't count.
 */
export type MetricDay = { iso: string; timezone: string };

/**
 * The value of each per-measurement metric shown as a Daily Monitoring column.
 * Sourced from six endpoints (computed / derived / vitals / basic medical data
 * / symptoms / ppgs) and reduced here to the most recent reading *of the day
 * being shown* per metric — a metric the patient didn't measure that day is
 * `null` rather than their last-ever value.
 */
export type PatientMetrics = {
  lvfp: MetricValue;
  hr: MetricValue;
  spo2: MetricValue;
  signal: MetricValue;
  /** Quality flag of the PPG the `signal` reading came from. */
  signalFlag: PPGQualityFlag | null;
  systolic: MetricValue;
  diastolic: MetricValue;
  weight: MetricValue;
  breathless: MetricValue;
  fatigue: MetricValue;
  relScore: MetricValue;
};

/** Raw arrays as returned by the six per-patient endpoints. */
export type RawMetricData = {
  computed: ListComputedWithPPGDataResponse["computed_data"];
  derived: ListPPGDerivedDataWithPPGDataResponse["derived_data"];
  /** `hf/vitals` — HR, systolic / diastolic BP, SpO2 only. */
  vitals: ListMedicalDataResponse["medical_data"];
  /** `hf/basic-med-data` — weight, dry weight, temperature, respiratory rate. */
  basics: ListMedicalDataResponse["medical_data"];
  /**
   * `symptoms` (the unfiltered list) — `hf/symptoms` carries only the HF-study
   * symptoms (shortness of breath, fatigue score), so perceived health change
   * has to come from here.
   */
  symptoms: ListSymptomResponse["symptoms"];
  ppgs: ListPPGDataResponse["ppg_data"];
};

/** Item in `items` with the newest parseable date, or `null` if none. */
function latest<T>(items: T[], getDate: (t: T) => string): T | null {
  let best: T | null = null;
  let bestTime = Number.NEGATIVE_INFINITY;
  for (const it of items) {
    const t = Date.parse(getDate(it));
    if (!Number.isNaN(t) && t >= bestTime) {
      bestTime = t;
      best = it;
    }
  }
  return best;
}

export function extractMetrics(
  raw: RawMetricData,
  day: MetricDay,
): PatientMetrics {
  // One formatter for the whole extraction: these arrays carry the patient's
  // history (the PPG list especially), so building one per item would cost more
  // than the reduction itself.
  const fmt = dayFormatter(day.timezone);
  const onDay = (datetime: string): boolean => {
    const t = Date.parse(datetime);
    return !Number.isNaN(t) && fmt.format(t) === day.iso;
  };
  /**
   * The day's newest item. The worklist reports what a patient measured today,
   * so a reading from an earlier day is no reading at all — a weight measured
   * once, weeks ago, otherwise read as today's.
   */
  const latestToday = <T>(items: T[], getDate: (t: T) => string): T | null =>
    latest(
      items.filter((it) => onDay(getDate(it))),
      getDate,
    );
  const computedOf = (measurementType: string): MetricValue => {
    const it = latestToday(
      raw.computed.filter((c) => c.measurement_type === measurementType),
      (c) => c.measurement_datetime,
    );
    return it
      ? { value: it.final_value, unit: it.measurement_unit ?? null }
      : null;
  };
  // A data group carries one row per aggregation function, all sharing a
  // measurement_datetime, so the group alone doesn't identify a value — select
  // the mean explicitly (the same row the patient-side HR/SpO2 tables show).
  const derivedOf = (group: keyof typeof DERIVED_MEAN_TYPE): MetricValue => {
    const it = latestToday(
      raw.derived.filter(
        (d) =>
          d.data_group === group &&
          d.measurement_type === DERIVED_MEAN_TYPE[group],
      ),
      (d) => d.measurement_datetime,
    );
    return it
      ? { value: it.final_value, unit: it.measurement_unit ?? null }
      : null;
  };
  const medDataOf = (
    source: ListMedicalDataResponse["medical_data"],
    measurementType: string,
  ): MetricValue => {
    const it = latestToday(
      source.filter((v) => v.measurement_type === measurementType),
      (v) => v.measurement_datetime,
    );
    // The medical-data union includes categorical (non-numeric) members; the
    // vitals we surface are all numeric, so guard rather than coerce.
    return it && typeof it.measurement_value === "number"
      ? { value: it.measurement_value, unit: it.measurement_unit ?? null }
      : null;
  };
  const symptomOf = (name: string): MetricValue => {
    const it = latestToday(
      raw.symptoms.filter((s) => s.symptom_name === name),
      (s) => s.symptom_datetime,
    );
    // The symptom union includes non-numeric (categorical) symptom values; the
    // three we surface are numeric scores, so guard rather than coerce.
    return it && typeof it.symptom_value === "number"
      ? { value: it.symptom_value, unit: null }
      : null;
  };
  // Signal reads both the SQI and the flag off the *same* PPG, so the indicator
  // can show "excluded" states (test / short signal) the SQI alone can't express.
  const latestPpg = latestToday(
    raw.ppgs.filter((p) => p.computed_sqi != null),
    (p) => p.measurement_datetime,
  );
  const signal: MetricValue =
    latestPpg?.computed_sqi != null
      ? { value: latestPpg.computed_sqi, unit: null }
      : null;

  return {
    lvfp: computedOf("left ventricular filling pressure"),
    hr: derivedOf(PPGDerivedDataGroup.hr),
    spo2: derivedOf(PPGDerivedDataGroup.spo2),
    systolic: medDataOf(raw.vitals, "blood pressure systolic"),
    diastolic: medDataOf(raw.vitals, "blood pressure diastolic"),
    weight: medDataOf(raw.basics, "weight"),
    breathless: symptomOf("shortness of breath"),
    fatigue: symptomOf("fatigue score"),
    relScore: symptomOf("perceived health change"),
    signal,
    signalFlag: latestPpg?.quality_flag ?? null,
  };
}
